import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const WidgetRenderer = ({ widget, onUpdate, onDelete, isSelected }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(widget?.content);

  const handleSave = () => {
    onUpdate(widget?.id, { content: editContent });
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditContent(widget?.content);
    setIsEditing(false);
  };

  const renderWidgetContent = () => {
    switch (widget?.type) {
      case 'author-bio':
        return (
          <div className="h-full p-4 bg-card rounded-lg border border-border">
            {isEditing ? (
              <div className="space-y-3">
                <input
                  type="text"
                  value={editContent?.name || ''}
                  onChange={(e) => setEditContent({...editContent, name: e?.target?.value})}
                  className="w-full p-2 border border-border rounded text-sm"
                  placeholder="Author Name"
                />
                <textarea
                  value={editContent?.bio || ''}
                  onChange={(e) => setEditContent({...editContent, bio: e?.target?.value})}
                  className="w-full p-2 border border-border rounded text-sm h-20 resize-none"
                  placeholder="Author biography..."
                />
              </div>
            ) : (
              <div className="flex space-x-4 h-full">
                <div className="flex-shrink-0">
                  <Image
                    src={widget?.content?.image}
                    alt={widget?.content?.imageAlt}
                    className="w-16 h-16 rounded-full object-cover"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-foreground text-sm mb-1">
                    {widget?.content?.name}
                  </h3>
                  <p className="text-xs text-muted-foreground leading-relaxed line-clamp-4">
                    {widget?.content?.bio}
                  </p>
                </div>
              </div>
            )}
          </div>
        );

      case 'author-headshot':
        return (
          <div className="h-full bg-card rounded-lg border border-border overflow-hidden">
            <div className="relative h-full">
              <Image
                src={widget?.content?.image}
                alt={widget?.content?.imageAlt}
                className="w-full h-full object-cover"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3">
                <p className="text-white text-xs font-medium">
                  {widget?.content?.caption}
                </p>
              </div>
            </div>
          </div>
        );

      case 'book-synopsis':
        return (
          <div className="h-full p-4 bg-card rounded-lg border border-border">
            {isEditing ? (
              <div className="space-y-2">
                <input
                  type="text"
                  value={editContent?.title || ''}
                  onChange={(e) => setEditContent({...editContent, title: e?.target?.value})}
                  className="w-full p-2 border border-border rounded text-sm"
                  placeholder="Book Title"
                />
                <textarea
                  value={editContent?.synopsis || ''}
                  onChange={(e) => setEditContent({...editContent, synopsis: e?.target?.value})}
                  className="w-full p-2 border border-border rounded text-sm h-20 resize-none"
                  placeholder="Book synopsis..."
                />
              </div>
            ) : (
              <div>
                <h3 className="font-semibold text-foreground text-sm mb-2">
                  {widget?.content?.title}
                </h3>
                <p className="text-xs text-muted-foreground leading-relaxed line-clamp-6 mb-2">
                  {widget?.content?.synopsis}
                </p>
                <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                  <span>{widget?.content?.genre}</span>
                  <span>•</span>
                  <span>{widget?.content?.pages} pages</span>
                </div>
              </div>
            )}
          </div>
        );

      case 'book-cover':
        return (
          <div className="h-full bg-card rounded-lg border border-border overflow-hidden">
            <div className="relative h-full">
              <Image
                src={widget?.content?.image}
                alt={widget?.content?.imageAlt}
                className="w-full h-full object-cover"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3">
                <h4 className="text-white font-semibold text-sm mb-1">
                  {widget?.content?.title}
                </h4>
                <p className="text-white/80 text-xs">
                  by {widget?.content?.author}
                </p>
              </div>
            </div>
          </div>
        );

      case 'video-player':
        return (
          <div className="h-full bg-card rounded-lg border border-border overflow-hidden">
            <div className="relative h-full">
              <div className="w-full h-32 bg-muted flex items-center justify-center">
                <div className="text-center">
                  <Icon name="Play" size={32} className="text-muted-foreground mx-auto mb-2" />
                  <p className="text-xs text-muted-foreground">{widget?.content?.title}</p>
                </div>
              </div>
              <div className="p-3">
                <p className="text-xs text-muted-foreground">
                  {widget?.content?.description}
                </p>
              </div>
            </div>
          </div>
        );

      case 'download-section':
        return (
          <div className="h-full p-4 bg-card rounded-lg border border-border">
            <h3 className="font-semibold text-foreground text-sm mb-3 flex items-center space-x-2">
              <Icon name="Download" size={14} />
              <span>{widget?.content?.title}</span>
            </h3>
            <div className="space-y-2">
              {widget?.content?.files?.slice(0, 3)?.map((file, index) => (
                <div key={index} className="flex items-center justify-between p-2 bg-muted/50 rounded text-xs">
                  <div className="flex items-center space-x-2">
                    <Icon name="FileText" size={12} />
                    <span className="font-medium">{file?.name}</span>
                  </div>
                  <div className="flex items-center space-x-2 text-muted-foreground">
                    <span>{file?.type}</span>
                    <span>•</span>
                    <span>{file?.size}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'company-info':
        return (
          <div className="h-full p-4 bg-card rounded-lg border border-border">
            <h3 className="font-semibold text-foreground text-sm mb-2">
              {widget?.content?.name}
            </h3>
            <p className="text-xs text-muted-foreground leading-relaxed mb-3 line-clamp-3">
              {widget?.content?.description}
            </p>
            <div className="space-y-1 text-xs text-muted-foreground">
              <div>Founded: {widget?.content?.founded}</div>
              <div>Location: {widget?.content?.location}</div>
            </div>
          </div>
        );

      default:
        return (
          <div className="h-full p-4 bg-card rounded-lg border border-border flex items-center justify-center">
            <div className="text-center">
              <Icon name="Package" size={24} className="text-muted-foreground mx-auto mb-2" />
              <p className="text-xs text-muted-foreground">Unknown Widget</p>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="relative w-full h-full group">
      {renderWidgetContent()}
      {/* Widget Controls */}
      {isSelected && (
        <div className="absolute -top-10 left-0 flex items-center space-x-1 bg-card border border-border rounded-md shadow-lg px-2 py-1">
          {!isEditing ? (
            <>
              <button
                onClick={() => setIsEditing(true)}
                className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors duration-200"
                title="Edit widget"
              >
                <Icon name="Edit2" size={14} />
              </button>
              <button
                onClick={() => onDelete(widget?.id)}
                className="p-1 rounded text-muted-foreground hover:text-destructive hover:bg-muted transition-colors duration-200"
                title="Delete widget"
              >
                <Icon name="Trash2" size={14} />
              </button>
              <div className="w-px h-4 bg-border mx-1" />
              <button
                className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors duration-200"
                title="Move widget"
              >
                <Icon name="Move" size={14} />
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleSave}
                className="p-1 rounded text-success hover:bg-muted transition-colors duration-200"
                title="Save changes"
              >
                <Icon name="Check" size={14} />
              </button>
              <button
                onClick={handleCancel}
                className="p-1 rounded text-muted-foreground hover:text-destructive hover:bg-muted transition-colors duration-200"
                title="Cancel editing"
              >
                <Icon name="X" size={14} />
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default WidgetRenderer;